import type { CharacterStay } from "@/data/rail-branches";
import { MARKER_DIAMETER } from "../../marker/MarkerMetrics/metrics";
import { markerCircleOffset, type MarkerCircleOffset } from "@/lib/timeline/scene/SceneMotion/motion";
import { placeGroupsAt } from "../RailPlaceGroups/groups";
import { staysAtPlace } from "../StayAt/byPlace";
import { staysOf } from "../StayAt/stay";

/** How far each extra craft in a stack sits from the one before, so they overlap. */
export const VEHICLE_STACK_STEP_PX = 44;
const GAP_BETWEEN_STACKS_PX = 32;
/** Room for a vehicle's name between the row and the top of the cast. */
const ROW_ABOVE_CAST_PX = MARKER_DIAMETER + 70;

/** Craft of one type share a stack: "bug-fighter-1" and "bug-fighter-2". */
const stackKey = (id: string): string => id.replace(/-\d+$/, "");

/** The row opens a slot this long before a vehicle arrives, so it never lands on another. */
const SLOT_OPENS_BEFORE_MS = 8_000;

/** The vehicles at a site right now, left to right, with craft of one type together. */
export function vehicleStacksAt(
  stays: readonly CharacterStay[],
  rootPlaceId: string,
  playheadMs: number,
  arrivingWithinMs = 0,
): string[][] {
  const stacks = new Map<string, string[]>();
  const here = staysAtPlace(stays, rootPlaceId)
    .filter((stay) => (
      stay.vehicle === true
      && stay.startMs <= playheadMs + arrivingWithinMs
      && playheadMs < stay.endMs
    ))
    .map((stay) => stay.characterId);
  // In order of arrival, so a newcomer's slot is on the side it comes from and it crosses nobody.
  const firstStart = (id: string): number => Math.min(
    ...staysOf(stays, id).map((stay) => stay.startMs),
  );
  const inOrder = [...new Set(here)]
    .sort((a, b) => firstStart(a) - firstStart(b) || a.localeCompare(b));
  for (const id of inOrder) {
    stacks.set(stackKey(id), [...(stacks.get(stackKey(id)) ?? []), id]);
  }
  return [...stacks.values()];
}

/** The top of the cast's circles, or where a lone person would sit if nobody is there. */
function castTopDy(
  stays: readonly CharacterStay[],
  rootPlaceId: string,
  playheadMs: number,
): number {
  const seats = placeGroupsAt(stays, rootPlaceId, playheadMs).flatMap((group) => (
    group.characterIds.map((_, index) => markerCircleOffset(index, group.characterIds.length).dy)
  ));
  return seats.length === 0 ? markerCircleOffset(0, 1).dy : Math.min(...seats);
}

interface RowVisit {
  startMs: number;
  endMs: number;
  stacks: string[][];
  dy: number;
}

const visitsByStays = new WeakMap<readonly CharacterStay[], Map<string, RowVisit[]>>();

/** One unbroken run of vehicles at a site, with every craft that ever joins it. */
function rowVisits(stays: readonly CharacterStay[], rootPlaceId: string): RowVisit[] {
  const atSite = staysAtPlace(stays, rootPlaceId);
  const craft = atSite.filter((stay) => stay.vehicle === true)
    .sort((a, b) => a.startMs - b.startMs);
  const visits: RowVisit[] = [];
  for (const stay of craft) {
    const last = visits.at(-1);
    if (last !== undefined && stay.startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, stay.endMs);
    } else {
      visits.push({ startMs: stay.startMs, endMs: stay.endMs, stacks: [], dy: 0 });
    }
  }
  for (const visit of visits) {
    const inVisit = craft.filter((stay) => (
      stay.startMs >= visit.startMs && stay.endMs <= visit.endMs
    ));
    const lengthMs = visit.endMs - visit.startMs;
    visit.stacks = vehicleStacksAt(inVisit, rootPlaceId, visit.startMs, lengthMs);
    const moments = atSite.map((stay) => stay.startMs)
      .filter((ms) => ms >= visit.startMs && ms < visit.endMs);
    const tops = [visit.startMs, ...moments].map((ms) => castTopDy(stays, rootPlaceId, ms));
    visit.dy = Math.min(...tops) - ROW_ABOVE_CAST_PX;
  }
  return visits;
}

function rowVisitAt(
  stays: readonly CharacterStay[],
  rootPlaceId: string,
  playheadMs: number,
): RowVisit | undefined {
  let bySite = visitsByStays.get(stays);
  if (bySite === undefined) {
    bySite = new Map();
    visitsByStays.set(stays, bySite);
  }
  let visits = bySite.get(rootPlaceId);
  if (visits === undefined) {
    visits = rowVisits(stays, rootPlaceId);
    bySite.set(rootPlaceId, visits);
  }
  return visits.find((visit) => visit.startMs <= playheadMs && playheadMs < visit.endMs);
}

/**
 * A vehicle's seat, above the cast and overlapping others of its type. Slots are held for the
 * whole visit, so a craft holds still as others arrive and leave.
 */
export function vehicleSeat(
  stays: readonly CharacterStay[],
  characterId: string,
  rootPlaceId: string,
  playheadMs: number,
): MarkerCircleOffset {
  const visit = rowVisitAt(stays, rootPlaceId, playheadMs);
  const stacks = visit?.stacks ?? vehicleStacksAt(stays, rootPlaceId, playheadMs);
  const widths = stacks.map((stack) => (
    MARKER_DIAMETER + (stack.length - 1) * VEHICLE_STACK_STEP_PX
  ));
  const total = widths.reduce((sum, width) => sum + width, 0)
    + GAP_BETWEEN_STACKS_PX * Math.max(0, stacks.length - 1);
  const dy = visit?.dy ?? castTopDy(stays, rootPlaceId, playheadMs) - ROW_ABOVE_CAST_PX;
  let left = -total / 2;
  for (const [index, stack] of stacks.entries()) {
    const at = stack.indexOf(characterId);
    if (at !== -1) {
      return { dx: left + MARKER_DIAMETER / 2 + at * VEHICLE_STACK_STEP_PX, dy };
    }
    left += (widths[index] ?? 0) + GAP_BETWEEN_STACKS_PX;
  }
  return { dx: 0, dy };
}

/** How far ahead a vehicle waits on the timeline. Further than any automatic zoom can see. */
export const VEHICLE_AHEAD_MS = 32_000;

/** The stay a vehicle is about to start, while it waits ahead for the playhead to reach it. */
export function upcomingVehicleStay(
  stays: readonly CharacterStay[],
  characterId: string,
  playheadMs: number,
): CharacterStay | undefined {
  return staysOf(stays, characterId)
    .filter((stay) => (
      stay.vehicle === true
      && stay.startMs > playheadMs
      && stay.startMs - playheadMs <= VEHICLE_AHEAD_MS
    ))
    .sort((a, b) => a.startMs - b.startMs)[0];
}

/** When a vehicle last joined or left this site's row, if that was within the last moment. */
export function recentVehicleChangeMs(
  stays: readonly CharacterStay[],
  rootPlaceId: string,
  playheadMs: number,
  withinMs: number,
): number | undefined {
  let latest: number | undefined;
  for (const stay of staysAtPlace(stays, rootPlaceId)) {
    if (stay.vehicle !== true) {
      continue;
    }
    for (const at of [stay.startMs - SLOT_OPENS_BEFORE_MS, stay.endMs]) {
      if (at <= playheadMs && playheadMs - at < withinMs && (latest === undefined || at > latest)) {
        latest = at;
      }
    }
  }
  return latest;
}

/** How soon before it arrives the camera starts leaving room for a vehicle's row. */
const ROW_ROOM_AHEAD_PX = 10_000 / 12;

/** Height only: the frame makes room for the row, without pulling back to fit it sideways. */
export function parkedVehicleNeedsRoom(pose: { dx: number; parked?: boolean }): boolean {
  return pose.parked === true && pose.dx <= ROW_ROOM_AHEAD_PX;
}
